/**
 * Hardware placement (tier 2, PLAN §6): fixed-scale pieces at `hardware[].at`, never scaled.
 * Each point is attached to the nearest face of its part; the piece's +y is that face's outward
 * normal and its +x follows the per-kind rule below. Pure maths; no Three.js.
 */
import { boxOf, type Vec3 } from './joints';
import type { KitAssemblyInput, KitHardwarePoint } from './types';

export const HARDWARE_ASSET: Record<string, string> = {
  screw: 'hw.screw',
  dowel: 'hw.dowel',
  'shelf-pin': 'hw.shelf-pin',
  'l-bracket': 'hw.l-bracket',
  'wall-strap': 'hw.wall-strap',
  'levelling-foot': 'hw.levelling-foot',
  'turned-foot': 'hw.turned-foot',
  // the grid family's anti-tip fixing: an L-bracket under the top, against the wall
  'wall-bracket': 'hw.l-bracket',
  // doors and drawers (src/families/fronts.ts)
  'cup-hinge': 'hw.cup-hinge',
  'butt-hinge': 'hw.butt-hinge',
  knob: 'hw.knob',
  'bar-handle': 'hw.bar-handle',
  // fixed length (400 mm runner, 500 mm track): the kit never stretches hardware
  'drawer-runner': 'hw.drawer-runner',
  'sliding-track': 'hw.sliding-track',
};

export interface HardwarePlacement {
  assetId: string;
  kind: string;
  partId: string;
  /** Metres, placement frame. */
  origin: Vec3;
  x: Vec3;
  y: Vec3;
  z: Vec3;
  /** Distance (mm) the point was moved to reach the face. */
  snappedMm: number;
}

const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

/** Outward normal of the face of the part box nearest the point, and the point snapped onto it. */
export function nearestFace(box: { lo: Vec3; hi: Vec3 }, p: Vec3): { normal: Vec3; point: Vec3; distance: number } {
  let best = { axis: 0, sign: 1, d: Infinity };
  for (let axis = 0; axis < 3; axis++) {
    for (const [plane, sign] of [[box.lo[axis], -1], [box.hi[axis], 1]] as const) {
      const d = Math.abs(p[axis] - plane);
      if (d < best.d) best = { axis, sign, d };
    }
  }
  const normal: Vec3 = [0, 0, 0];
  normal[best.axis] = best.sign;
  const point: Vec3 = [...p];
  point[best.axis] = best.sign > 0 ? box.hi[best.axis] : box.lo[best.axis];
  for (let i = 0; i < 3; i++) if (i !== best.axis) point[i] = Math.min(box.hi[i], Math.max(box.lo[i], point[i]));
  return { normal, point, distance: best.d };
}

/** What a rule may need beyond the face normal. */
export interface AlongContext {
  /** Role of the part the piece is fixed to (`door`, `drawer-front`, `slider`, ...). */
  role?: string;
  /** Unit vector, in the face, from the point towards the nearer side edge of the part (hinges). */
  edge?: Vec3;
}

/** The side edge (along world x, or z on a side-facing face) of the part nearest the point, as a unit vector. */
export function sideEdgeToward(box: { lo: Vec3; hi: Vec3 }, n: Vec3, p: Vec3): Vec3 | undefined {
  if (Math.abs(n[1]) > 0.5) return undefined;
  const axis = Math.abs(n[2]) > 0.5 ? 0 : 2;
  const e: Vec3 = [0, 0, 0];
  e[axis] = p[axis] - box.lo[axis] <= box.hi[axis] - p[axis] ? -1 : 1;
  return e;
}

/**
 * The piece's +x for a face normal n (+y), per kind. Rules that the face alone cannot settle use
 * the context: a bar handle runs along the door's height but along a drawer front's width (the
 * part's role); a hinge's +z (arm, knuckle line) must point at the hinge edge, which on the door's
 * back face is the nearer side edge of the door (or of the glazed door's stile).
 */
export function alongFor(kind: string, n: Vec3, ctx: AlongContext = {}): Vec3 {
  const vertical = Math.abs(n[1]) > 0.5;
  switch (kind) {
    case 'cup-hinge':
    case 'butt-hinge':
      // +x along the door's height, signed so that +z = x × y points at the hinge edge: x = y × edge
      return ctx.edge && !vertical ? cross(n, ctx.edge) : vertical ? [1, 0, 0] : [0, 1, 0];
    case 'bar-handle':
      // along the height of a door or sliding panel; along the width of a drawer front
      return ctx.role === 'drawer-front' || vertical ? [1, 0, 0] : [0, 1, 0];
    case 'drawer-runner':
      // the run is front to back, on the drawer side (a face pointing ±x)
      return Math.abs(n[2]) > 0.5 ? [1, 0, 0] : [0, 0, 1];
    case 'sliding-track':
      // the run is across the piece, on the front edge of the board above or below the panels
      return Math.abs(n[0]) > 0.5 ? [0, 0, 1] : [1, 0, 0];
    case 'l-bracket':
      // leg 1 runs down the face; on a horizontal face, towards the front
      return vertical ? [0, 0, 1] : [0, -1, 0];
    case 'wall-strap':
      // leg 1 runs into the piece from the wall (+z); on a vertical face, downwards
      return vertical ? [0, 0, 1] : [0, -1, 0];
    default:
      return vertical ? [1, 0, 0] : [0, 1, 0];
  }
}

export function hardwarePlacements(input: KitAssemblyInput, { includeHidden = false } = {}): { placements: HardwarePlacement[]; skipped: string[] } {
  const parts = new Map(input.parts.map((p) => [p.id, p]));
  const placements: HardwarePlacement[] = [];
  const skipped: string[] = [];
  for (const h of input.hardware ?? []) {
    const assetId = HARDWARE_ASSET[h.kind];
    if (!assetId) {
      skipped.push(`${h.kind}: no kit piece`);
      continue;
    }
    if (h.visible === false && !includeHidden) continue;
    h.at.forEach((at: KitHardwarePoint, i) => {
      const part = parts.get(at.partId);
      if (!part) {
        skipped.push(`${h.kind}[${i}]: unknown part ${at.partId}`);
        return;
      }
      const box = boxOf(part);
      const f = nearestFace(box, [at.x, at.y, at.z]);
      const y = f.normal;
      // dowels sit centred on the joint plane, not on top of the face
      const x = alongFor(h.kind, y, { role: part.role, edge: sideEdgeToward(box, y, f.point) });
      const z = cross(x, y);
      placements.push({
        assetId, kind: h.kind, partId: part.id,
        origin: [f.point[0] / 1000, f.point[1] / 1000, f.point[2] / 1000],
        x, y, z, snappedMm: f.distance,
      });
    });
  }
  return { placements, skipped };
}
